import type { BasicObservable } from '@doxyme/extensions-bridge';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * Utility hook to use a {@link BasicObservable} as a React state component. Handles the initial value, updating the state when the observable changes, and unsubscribing when the component unmounts.
 * @param observable
 * @returns The current value of the observable and a setter function to update the value.
 * @example
 * ```tsx
 * const [applets] = useBasicObservable<HTMLIFrameElement[]>(
 *  callFrameApplet.getFrames(),
 * );
 * ```
 */
export function useBasicObservable<ObservableType>(
  observable: BasicObservable<ObservableType>,
): [state: ObservableType, setState: Dispatch<SetStateAction<ObservableType>>] {
  const [state, setState] = useState<ObservableType>(() => observable.value);

  useEffect(() => {
    setState(observable.value);
    return observable.onChange(() => {
      setState(observable.value);
    });
  }, [setState, observable]);

  return [state, setState];
}
